import { useCallback, useState } from "react";

export interface DriveFilesNode<T> {
  name: string;
  // The group id then the folder path under it, so same-named folders in two groups differ.
  key: string;
  children: Map<string, DriveFilesNode<T>>;
  files: T[];
}

interface DriveTreeFile {
  drive_path: string | null;
  original_name: string;
}

export interface DriveFilesTreeOptions<T> {
  groupIdOf: (file: T) => string | null;
  trackedGroupId: string;
  // Synced root folder id to its label.
  rootNames: ReadonlyMap<string, string>;
  // Group ids shown even with no files, such as a configured root before its first sync.
  alwaysShow?: Iterable<string>;
}

export const TRACKED_FILES_LABEL = "Tracked files";

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

const makeNode = <T>(name: string, key: string): DriveFilesNode<T> => ({
  name,
  key,
  children: new Map(),
  files: [],
});

// One group per synced root, keyed by id so two same-named roots stay apart, then "Tracked
// files" last. A root file's drive_path starts with the root's own name, so only the folders
// after it nest; a tracked file's path runs from the top of the shared drive, so all of its
// folders nest. A file under no current root (one removed since) is grouped by its first
// folder, so it still shows.
export const buildDriveFilesTree = <T extends DriveTreeFile>(
  files: readonly T[],
  { groupIdOf, trackedGroupId, rootNames, alwaysShow = [] }: DriveFilesTreeOptions<T>,
): DriveFilesNode<T>[] => {
  const groups = new Map<string, DriveFilesNode<T>>();
  const group = (key: string, name: string) => {
    const existing = groups.get(key);
    if (existing) return existing;
    const created = makeNode<T>(name, key);
    groups.set(key, created);
    return created;
  };
  const trackedGroup = () => group(trackedGroupId, TRACKED_FILES_LABEL);
  for (const id of alwaysShow) {
    if (id === trackedGroupId) trackedGroup();
    else group(id, rootNames.get(id) || "Folder");
  }
  for (const file of files) {
    const folders = (file.drive_path ?? "").split("/").filter(Boolean).slice(0, -1);
    const id = groupIdOf(file);
    let cursor: DriveFilesNode<T>;
    let nested = folders.slice(1);
    if (id === trackedGroupId) {
      cursor = trackedGroup();
      nested = folders;
    } else if (id && rootNames.has(id)) {
      cursor = group(id, rootNames.get(id) || folders[0] || "Folder");
    } else {
      cursor = group(`unrooted:${folders[0] ?? ""}`, folders[0] ?? "Other files");
    }
    for (const part of nested) {
      let child = cursor.children.get(part);
      if (!child) {
        child = makeNode<T>(part, `${cursor.key}/${part}`);
        cursor.children.set(part, child);
      }
      cursor = child;
    }
    cursor.files.push(file);
  }
  for (const top of groups.values()) collapseSingleChildChains(top);
  return [...groups.values()].sort((a, b) => {
    const aTracked = a.key === trackedGroupId;
    const bTracked = b.key === trackedGroupId;
    if (aTracked !== bTracked) return aTracked ? 1 : -1;
    return byName(a.name, b.name) || a.key.localeCompare(b.key);
  });
};

// Below each group, a folder holding only one subfolder and no files folds into it, so a deep
// path to a lone file reads as one row ("Clients/Amazon/PPA/MSPPA/Executed"). The folded row
// takes the deepest folder's key, so its open state survives a refetch. Groups stay whole:
// their label names the synced folder or "Tracked files".
const collapseSingleChildChains = <T>(node: DriveFilesNode<T>): void => {
  const folded = new Map<string, DriveFilesNode<T>>();
  for (const [part, child] of node.children) {
    let current = child;
    let name = child.name;
    while (current.files.length === 0 && current.children.size === 1) {
      const [only] = current.children.values();
      name = `${name}/${only.name}`;
      current = only;
    }
    collapseSingleChildChains(current);
    folded.set(part, current === child ? child : { ...current, name });
  }
  node.children = folded;
};

export const countTreeFiles = <T>(node: DriveFilesNode<T>): number =>
  [...node.children.values()].reduce((sum, child) => sum + countTreeFiles(child), node.files.length);

export const sortedChildren = <T>(node: DriveFilesNode<T>): DriveFilesNode<T>[] =>
  [...node.children.values()].sort((a, b) => byName(a.name, b.name));

export const sortedFiles = <T extends DriveTreeFile>(node: DriveFilesNode<T>): T[] =>
  [...node.files].sort((a, b) => byName(a.original_name, b.original_name));

// Top-level groups start open and deeper folders closed; a toggle flips that default, so a
// refetch never re-opens a group the operator closed.
export const useDriveTreeOpenState = () => {
  const [toggled, setToggled] = useState<ReadonlySet<string>>(new Set());
  const toggle = useCallback(
    (key: string) =>
      setToggled((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );
  const isOpen = useCallback(
    (node: { key: string }, depth: number) => (depth === 0) !== toggled.has(node.key),
    [toggled],
  );
  return { isOpen, toggle };
};
